// Catálogo de "Vistas útiles": dashboards que cruzan varios datasets en un pantallazo.
// Fuente única de verdad usada por el sidebar (useDashboardNavGroups) y por el
// configurador (/vistas/configurador).
//
// Tres niveles de activación (de más fuerte a más débil):
//   - Empresa (master)        → empresa.vistas_config = { [id]: boolean }. Default: activada.
//   - Empresa por usuario      → empresa.vistas_usuarios = { [id]: { ocultosPara: [userId, ...] } }.
//                                Lo decide un admin de usuarios; el usuario NO lo puede revertir.
//   - Usuario (self)           → user.vistas_ocultas = [id, ...]. Blacklist personal.
// Una vista se ve si: la empresa la tiene activa, el admin no la bloqueó para ese usuario,
// y el usuario no la ocultó para sí mismo.
//
// Vistas personalizadas (TAR-745): una vista puede declarar además
//   - `permiso`:  hay que tener ese permiso para verla. Sin el campo → sólo admins.
//                 Es lo que hace que una vista nacida para un cliente se le pueda dar
//                 después a cualquier otro que la pida: se le tilda el permiso y listo.
//   - `empresas`: si está, sólo esas empresas la tienen en el catálogo, ni siquiera con
//                 el permiso. Sin el campo → disponible para todas. Hoy no la usa ninguna.
// Convención para sumar una vista: entrada acá, página en `pages/vistas/<id>.js` y
// componentes en `sections/<id>/`.

export const VISTAS_UTILES = [
  {
    // TAR-784: dejó de ser visible por default; soporte tilda el permiso a quien la pida.
    id: 'vista-obra',
    title: 'Vista de obra',
    path: '/vistas/obra',
    descripcion: 'Presupuestado vs gastado vs cobrado, por obra o cartera. Todo dolarizado.',
    permiso: 'VER_VISTA_OBRA',
  },
  {
    // Nació para Dipsa (TAR-745) pero cualquier empresa la puede adoptar: el control
    // es el permiso, que se le da a la que la pida.
    id: 'costo-m2',
    title: 'Costo por m²',
    path: '/vistas/costo-m2',
    descripcion: 'Cuánto cuesta cada m² de la obra en USD, proyección al final y desvío contra el objetivo.',
    permiso: 'VER_COSTO_M2',
  },
  {
    // Nació para Grupo Hea: una constructora que arrancó la obra sin presupuesto
    // y sólo necesita saber cuánto gastó en cada rubro. Por eso la vista no tiene
    // avance, ni certificados, ni margen — en una obra así cada uno de esos
    // bloques es una fila más de ceros.
    id: 'gasto-obra',
    title: 'Vista simplificada de obra',
    path: '/vistas/gasto-obra',
    descripcion: 'Cuánto se gastó en cada rubro, subrubro e ítem, en USD al cambio de cada factura, con los acuerdos por proveedor y lo que falta pagarles.',
    permiso: 'VER_GASTO_OBRA',
  },
  {
    // TAR-759. Nació para un cliente que analiza su cartera en dólares, pero como
    // el gate es el permiso, se le puede dar a cualquier otro que la pida.
    id: 'cajas-consolidado',
    title: 'Cajas – Análisis consolidado',
    path: '/vistas/cajas-consolidado',
    descripcion: 'Movimientos de una o varias obras en ARS o USD, con el gasto abierto por categoría y subcategoría.',
    permiso: 'VER_CAJAS_CONSOLIDADO',
  },
  {
    id: 'caja-financiera',
    title: 'Caja financiera por proyecto',
    path: '/vistas/caja-financiera',
    descripcion: 'Ingresos, egresos, impuestos registrados y saldo del proyecto, con evolución mensual.',
    permiso: 'VER_CAJA_FINANCIERA',
  },
  {
    // Nació para Fuster (no se acomodaba al flujo estándar de Caja) pero es genérica:
    // el control es el permiso, que se le da a cualquier otra empresa que la pida.
    id: 'detalle-movimientos',
    title: 'Detalle de movimientos',
    path: '/vistas/detalle-movimientos',
    descripcion: 'Movimientos individuales por código de operación, con filtro por proveedor y totales por moneda.',
    permiso: 'VER_DETALLE_MOVIMIENTOS',
  },
  {
    // TAR-1165. Nació para Fuster: seguir qué gastos pagó el cliente, cuáles adelantó
    // la empresa y cuánto falta que le reintegren. La modalidad sale de los tags extra.
    id: 'gastos-reintegros',
    title: 'Gastos y reintegros',
    path: '/vistas/gastos-reintegros',
    descripcion: 'Gastos de caja por modalidad con el cliente: pago directo, pendiente de reintegro y reintegrado.',
    permiso: 'VER_GASTOS_REINTEGROS',
  },
];

export function getVistaUtil(id) {
  return VISTAS_UTILES.find((v) => v.id === id) || null;
}

// ¿La empresa tiene activada esta vista? Sin config previa → activada por default.
export function vistaHabilitadaEmpresa(empresa, id) {
  const cfg = empresa?.vistas_config;
  if (!cfg || cfg[id] === undefined) return true;
  return cfg[id] !== false;
}

// ¿El usuario ocultó esta vista para sí mismo?
export function vistaOcultaUsuario(user, id) {
  return (user?.vistas_ocultas || []).includes(id);
}

// ¿Un admin bloqueó esta vista para este usuario puntual? (decisión de empresa, no revertible por el usuario)
export function vistaBloqueadaParaUsuario(empresa, id, userId) {
  if (userId == null) return false;
  const lista = empresa?.vistas_usuarios?.[id]?.ocultosPara || [];
  return lista.includes(userId);
}

// Catálogo de vistas que le corresponden a una empresa: las generales más las que
// fueron hechas para ella. Es lo que muestra el configurador.
export function vistasDeEmpresa(empresa) {
  return VISTAS_UTILES.filter((v) => !v.empresas || v.empresas.includes(empresa?.id));
}

// Vistas que la empresa tiene efectivamente habilitadas: las de su catálogo cuya
// acción está configurada. Es lo único que tiene sentido configurar a nivel empresa
// — una vista sin la acción no la ve nadie por más que el master esté en "Activa",
// y mostrarla en el configurador hace creer lo contrario.
export function vistasLicenciadasParaEmpresa(empresa) {
  const acciones = empresa?.acciones || [];
  return vistasDeEmpresa(empresa).filter((v) => !v.permiso || acciones.includes(v.permiso));
}

// ¿El usuario tiene el permiso que la vista exige? Las vistas sin `permiso` no piden
// ninguno en particular (el sidebar las reserva para admins, como siempre).
export function vistaPermitidaParaUsuario(vista, permisos = []) {
  return !vista.permiso || permisos.includes(vista.permiso);
}

// Vistas efectivamente visibles para un (empresa, usuario).
export function vistasVisiblesPara(empresa, user, permisos = []) {
  return vistasDeEmpresa(empresa).filter(
    (v) =>
      vistaHabilitadaEmpresa(empresa, v.id) &&
      !vistaBloqueadaParaUsuario(empresa, v.id, user?.id) &&
      !vistaOcultaUsuario(user, v.id) &&
      vistaPermitidaParaUsuario(v, permisos),
  );
}
